"use client";

import React from "react";
import { Box, Typography, Stack, Radio } from "@mui/material";
import {
  ColletteButton,
  ColletteChip,
  ColletteTextButton
} from "@collette-travel/collette-common-controls";
import { useLocale, useTranslations } from "next-intl";
import { TourDate } from "@/types/dto/pdp/pdp-dates-inclusions-pricing";

interface DepartureDateRowDesktopProps {
  index?: number;
  tourDate: TourDate;
  isSelected: boolean;
  onSelect: (tourDate: TourDate) => void;
  onRequestQuote?: (tourDate: TourDate) => void;
  onViewItinerary?: (tourDate: TourDate) => void;
  onBookNow?: (tourDate: TourDate) => void;
  onHover?: (tourDate: TourDate) => void;
  lastItem: boolean;
  hasSpecialEvent: boolean;
  hasReverseItinerary: boolean;
  isSoldOut: boolean;
  isCallToBook: boolean;
  isSeatsRemainingThreshold: boolean;
  showStatusMessage: boolean;
  hasOffer: boolean;
  formattedDate: string;
  offerBadgeText: string;
}

const DepartureDateRow = ({
  index,
  tourDate,
  isSelected,
  onSelect,
  onRequestQuote,
  onViewItinerary,
  onBookNow,
  onHover,
  lastItem,
  hasSpecialEvent,
  isSoldOut,
  isCallToBook,
  isSeatsRemainingThreshold,
  showStatusMessage,
  formattedDate,
  offerBadgeText,
  hasReverseItinerary,
  hasOffer
}: DepartureDateRowDesktopProps) => {
  const t = useTranslations("PDP");
  const isNewZealandLocale = useLocale() === "en-nz";
  console.log("isSoldout", isSoldOut, "isCallToBook", isCallToBook, "isNewZealandLocale", isNewZealandLocale);

  return (
    <Box
      role="button"
      tabIndex={-1}
      onMouseEnter={() => onHover?.(tourDate)}
      onClick={() => onSelect(tourDate)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(tourDate);
        }
      }}
      sx={{
        backgroundColor: isSelected ? "background.cool_strong" : "transparent",
        border: isSelected ? "2px solid" : "none",
        borderColor: "primary.main",
        borderBottom: (theme) => (!isSelected ? `1px solid ${theme.palette.divider}` : undefined),
        padding: "1.2rem",
        paddingBottom: tourDate.statusMessage && "1.5rem",
        cursor: "pointer",
        display: { xs: "none", sm: "flex" },
        flexDirection: "column",
        borderBottomLeftRadius: lastItem ? "2rem" : 0,
        borderBottomRightRadius: lastItem ? "2rem" : 0,
        position: "relative"
      }}
      data-testid={`departure-date-row-desktop_${index}`}
    >
      {/* Main Row Content - Date + Price always horizontal */}
      <Stack direction="row" justifyContent="space-between" alignItems="start" width="100%">
        {/* Left section: Date + Price */}
        <Stack
          direction="row"
          alignItems="start"
          flex={1}
          maxWidth={tourDate.strikeOutPrice && hasOffer ? "65%" : "58%"}
        >
          {/* Radio + Date */}
          <Stack direction="row" alignItems="start" width="100%">
            <Radio
              checked={isSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onSelect(tourDate)}
              data-testid={`departure-date_${index}_radio`}
              sx={{
                color: "primary.main",
                "&.Mui-checked": {
                  color: "primary.main"
                }
              }}
            />
            <Stack
              sx={{
                alignItems: "start",
                width: "100%"
              }}
            >
              <Stack
                sx={{
                  alignItems: "center",
                  width: "100%",
                  flexDirection: "row",
                  marginTop: "0.9rem"
                }}
              >
                <Typography
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 500,
                    color: isSoldOut ? "text.disabled" : "text.primary",
                    width: { sm: "20rem", md: "34rem", lg: "20rem" }
                  }}
                  data-testid={`departure-date_${index}`}
                >
                  {formattedDate}
                </Typography>
                {/* Price */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 700,
                      color: "secondary.main",
                      textAlign: "left",
                      minWidth: "6rem"
                    }}
                    data-testid={`departure-date_${index}_tour-price`}
                  >
                    {tourDate.offerPrice || tourDate.doublePrice}
                  </Typography>
                  {tourDate.strikeOutPrice && hasOffer && (
                    <Typography
                      sx={{
                        fontSize: "1.6rem",
                        fontWeight: 700,
                        color: "text.disabled",
                        textDecoration: "line-through",
                        visibility: tourDate.strikeOutPrice ? "visible" : "hidden"
                      }}
                      data-testid={`departure-date_${index}_tour-strikeout-price`}
                    >
                      {tourDate.strikeOutPrice}
                    </Typography>
                  )}
                </Stack>
              </Stack>
              {((tourDate.statusMessage && showStatusMessage) || hasSpecialEvent) && (
                <Stack
                  sx={{
                    display: "block"
                  }}
                >
                  {/* Status Message */}
                  {tourDate.statusMessage && showStatusMessage && (
                    <Typography
                      sx={{
                        fontSize: "1.4rem",
                        fontWeight: 700,
                        color: isSoldOut || isSeatsRemainingThreshold ? "error.main" : "secondary.main",
                        textDecoration: isCallToBook && !isSeatsRemainingThreshold ? "underline" : "none",
                        marginBottom: 0
                      }}
                      data-testid={`departure-date_${index}_status`}
                    >
                      {tourDate.statusMessage}
                    </Typography>
                  )}

                  {/* Reverse Itinerary if special event is also true (edge case) */}
                  {hasReverseItinerary && hasSpecialEvent && (
                    <Typography
                      sx={{
                        fontSize: "1.2rem",
                        fontWeight: 500,
                        color: "primary.main"
                      }}
                      data-testid={`departure-date_${index}_reverse-itinerary-tour`}
                    >
                      {t("reverseItinerary")}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: "30%",
            justifyContent: "flex-end"
          }}
        >
          <Box
            display="flex"
            width="100%"
            flexDirection="row"
            justifyContent={hasSpecialEvent || hasReverseItinerary ? "space-between" : "flex-end"}
            alignItems="center"
          >
            {/* Tablet/Desktop: Special Event Badge (vertical stack) */}
            {hasSpecialEvent && (
              <Box
                sx={{
                  minWidth: "5rem",
                  display: "flex",
                  justifyContent: "center",
                  margin: "0 !important",
                  visibility: hasSpecialEvent ? "visible" : "hidden"
                }}
              >
                <Stack alignItems="center" spacing={0.5}>
                  <Box
                    component="img"
                    src="/images/inclusions/special-event.svg"
                    alt=""
                    sx={{ width: "2.3102rem", height: "1.272rem", flexShrink: 0 }}
                    data-testid={`departure-date_${index}_special-event-image`}
                  />
                  <Typography
                    sx={{
                      fontSize: "1.2rem",
                      fontWeight: 500,
                      color: "primary.main",
                      textAlign: "center",
                      lineHeight: "1.6rem",
                      maxWidth: "5rem"
                    }}
                    data-testid={`departure-date_${index}_special-event-label`}
                  >
                    {t("specialEvent")}
                  </Typography>
                </Stack>
              </Box>
            )}

            {/* Tablet/Desktop: Reverse Itinerary Badge (vertical stack) */}
            {hasReverseItinerary && !hasSpecialEvent && (
              <Box
                sx={{
                  minWidth: "5rem",
                  maxWidth: "6rem",
                  display: "flex",
                  justifyContent: "center",
                  visibility: hasReverseItinerary ? "visible" : "hidden"
                }}
              >
                <Stack alignItems="center" spacing={0.5}>
                  <Box
                    component="img"
                    src="/images/inclusions/reverse-itinerary.svg"
                    alt=""
                    sx={{
                      width: "2.4rem",
                      height: "2.4rem",
                      flexShrink: 0
                    }}
                    data-testid={`departure-date_${index}_reverse-itinerary-image`}
                  />
                  <Typography
                    sx={{
                      fontSize: "1.2rem",
                      fontWeight: 500,
                      color: "primary.main",
                      textAlign: "center",
                      lineHeight: "1.6rem"
                    }}
                    data-testid={`departure-date_${index}_reverse-itinerary`}
                  >
                    {t("reverseItinerary")}
                  </Typography>
                </Stack>
              </Box>
            )}

            {/* Offer Badge */}
            {hasOffer && offerBadgeText && (
              <Box
                sx={{
                  minWidth: "fit-content",
                  justifyContent: "center"
                }}
              >
                <ColletteChip
                  color="warning"
                  testId={`departure-date_${index}_offer-badge`}
                  label={offerBadgeText}
                  sx={{
                    fontSize: "1.2rem",
                    fontWeight: 600,
                    color: "primary.main",
                    whiteSpace: "nowrap",
                    borderRadius: "1rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Stack>

      {/* Expanded View - Single Supplement and CTAs */}
      {isSelected && (
        <>
          {/* Single Supplement */}
          <Stack direction="row" width={tourDate.strikeOutPrice && hasOffer ? "65%" : "58%"}>
            <Stack
              direction="row"
              data-testid="single-supplement-section"
              sx={{
                marginLeft: "4.2rem",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginTop: "1.6rem",
                width: "100%",
                flex: 1
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.4rem",
                  fontWeight: 600,
                  color: "text.primary",
                  width: { sm: "20rem", md: "34rem", lg: "20rem" },
                  textAlign: "right",
                  paddingRight: "1.2rem"
                }}
                data-testid={`departure-date_${index}_single-supplement-title`}
              >
                {t("singleSupplement")}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    color: "secondary.main",
                    minWidth: "6rem"
                  }}
                  data-testid={`departure-date_${index}_single-supplement-price`}
                >
                  {tourDate.singlePrice}
                </Typography>
                {tourDate.singleStrikeOutPrice && hasOffer && (
                  <Typography
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 700,
                      color: "text.secondary",
                      textDecoration: "line-through",
                      visibility: tourDate.singleStrikeOutPrice ? "visible" : "hidden"
                    }}
                    data-testid={`departure-date_${index}_single-supplement-strikeout-price`}
                  >
                    {tourDate.singleStrikeOutPrice}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>

          {/* CTAs */}
          <Stack
            gap="1.2rem"
            sx={{
              width: "100%",
              justifyContent: "flex-end",
              textWrap: "none",
              flexDirection: "row",
              marginTop: "1.6rem"
            }}
          >
            {onRequestQuote && (
              <ColletteTextButton
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onRequestQuote(tourDate);
                }}
                label={t("requestAQuote")}
                testId={`departure-date_${index}_request-quote-button`}
                color="primary"
                sx={{
                  fontSize: "1.4rem!important",
                  fontWeight: 600,
                  "&:focus-visible": {
                    outline: "1px solid",
                    outlineColor: "primary.main"
                  }
                }}
              />
            )}
            {onViewItinerary && (
              <ColletteButton
                onClick={(e) => {
                  console.log("View Itinerary clicked for date:", tourDate);
                  e.stopPropagation();
                  onViewItinerary(tourDate);
                }}
                testId={`departure-date_${index}_view-itinerary-button`}
                variant="outlined"
                size="large"
                sx={{
                  backgroundColor: "background.default"
                }}
                label={t("viewItinerary")}
              />
            )}
            {onBookNow && !isSoldOut && !isCallToBook && !isNewZealandLocale && (
              <ColletteButton
                onClick={(e) => {
                  e.stopPropagation();
                  onBookNow(tourDate);
                }}
                testId={`departure-date_${index}_book-now-button`}
                color="primary"
                size="large"
                label={t("bookNow")}
              />
            )}
            {!isSoldOut && isCallToBook && (
              <ColletteButton
                component="a"
                href={`tel:${tourDate.callForDetailsPhoneNumber}`}
                testId={`departure-date_${index}_call-to-book-button`}
                color="primary"
                size="large"
                label={`${t("Call to Book")} ${tourDate.callForDetailsPhoneNumber}`}
              />
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default DepartureDateRow;
